<template>
  <div
    class="border border-[#979797]/30 rounded-xl px-2.5 pt-3.5"
    :class="isExpanded ? 'pb-2.5' : 'pb-3.5'"
  >
    <div class="flex items-center justify-between mb-2">
      <div class="font-secondary text-[12px] leading-[16px] text-gray-500">
        Track {{ index + 1 }}
      </div>
      <div
        class="size-6 rounded-md flex items-center justify-center cursor-pointer hover:bg-gray-200/20"
        @click="isExpanded = !isExpanded"
      >
        <Icon
          name="ChevronUp"
          :size="18"
          class="stroke-gray-500"
          :class="{ 'rotate-180': !isExpanded }"
        />
      </div>
    </div>

    <div class="flex items-center gap-x-2.5">
      <div
        class="min-w-9 h-9 border border-gray-200 rounded-full flex items-center justify-center cursor-pointer"
        @click="handleClick"
      >
        <Icon :name="icon" :size="18" :style="iconStyles" />
      </div>
      <div ref="el" class="w-full"></div>
    </div>

    <div
      v-if="isExpanded && hasContent"
      class="mt-[22px] flex flex-col gap-[22px]"
    >
      <div v-if="track.summary">
        <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
          Summary
        </div>
        <div class="leading-[20px]">
          {{ track.summary }}
        </div>
      </div>

      <Attributes
        v-if="track.attributes.length"
        :attributes="track.attributes"
      />

      <Transcript
        v-if="transcriptEntries.length"
        :entries="transcriptEntries"
        :track-index="index"
      />

      <div v-if="track.stems.breakdown.length">
        <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
          Stems ({{ track.stems.breakdown.length }})
        </div>

        <div class="flex flex-col gap-y-[14px]">
          <Stem
            v-for="stem in track.stems.breakdown"
            :key="stem.name"
            :name="stem.name"
            :url="stem.url"
            :sheet-music="stem.sheetMusic?.preview"
            :track-index="index"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon, type IconName } from '@dacr/ui'
import Attributes from '@/shared/components/Attributes.vue'
import Transcript from '@/features/copyright/components/Transcript.vue'
import Stem from '@/features/copyright/components/Stem.vue'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { computed, onMounted, ref } from 'vue'

type VideoFileAnalysis = Extract<
  NonNullable<CopyrightFile['analysis']>,
  { __typename?: 'VideoAnalysis' }
>
type Track = VideoFileAnalysis['tracks'][number]

const props = defineProps<{
  track: Track
  index: number
}>()

const isExpanded = ref(false)

const transcriptEntries = computed(() => props.track.transcript.preview)
const hasContent = computed(
  () =>
    !!props.track.summary ||
    !!props.track.attributes.length ||
    !!transcriptEntries.value.length ||
    !!props.track.stems.breakdown.length
)

const el = ref<HTMLElement>()
const wavesurfer = ref<InstanceType<typeof import('wavesurfer.js').default>>()

const icon = ref<IconName>('Play')

const iconStyles = computed(() => {
  return {
    marginLeft: icon.value === 'Play' ? '3px' : null,
  }
})

const handleClick = () => {
  wavesurfer.value?.playPause()

  icon.value = wavesurfer.value?.isPlaying() ? 'Pause' : 'Play'
}

onMounted(async () => {
  if (!el.value) return

  const WaveSurfer = (await import('wavesurfer.js')).default

  wavesurfer.value = WaveSurfer.create({
    container: el.value,
    waveColor: '#AFB5C0',
    progressColor: '#222',
    url: props.track.url,
    height: 40,
    cursorWidth: 0,
  })
})
</script>
